import { memo, useRef, useCallback, useMemo } from 'react';
import { BaseFormModal } from '@/components/BaseFormModal';
import { Dropdownlist } from '@/components/Dropdownlist';
import { MultiSelect } from '@/components/MultiSelect';
import { DateTimePicker } from '@/components/DateTimePicker';
import { TimePicker } from '@/components/TimePicker';
import moment from 'moment';
import {
  RECURRENCE_DAY_CODES,
  RECURRENCE_DAY_LABELS,
  formatHhmmForDisplay,
  hhmmToMinutes,
  type AdvisoryFieldErrors,
  type RecurrenceDayCode,
} from './advisoryValidation';
import { formatRecurrenceDays } from './advisorySchedule';
import { getOfficerLabel, getOfficerPluralLabel } from '@/utils/auth';

export interface EditingAdvisory {
  id: number | null;
  displayId: number | null;
  advisoryTypeId: number | null;
  recipientType: string;
  npsdClient: number | null;
  selectedSiteIds: number[];
  subject: string;
  details: string;
  location: string;
  /** Urgent = requires per-officer acknowledgment, pinned first on the board. */
  urgent: boolean;
  isTemporary: boolean;
  /** When temporary: false = one-time (start/end), true = recurring weekly window. */
  recurring: boolean;
  recurrenceDays: string[];
  /** "HH:mm" 24h local wall-clock times (only meaningful when recurring). */
  recurrenceStartTime: string;
  recurrenceEndTime: string;
  startDateTime: Date | null;
  endDateTime: Date | null;
  attachmentList: any[];
  resizedImageList: any[];
  attachmentInfoList: any[];
}

export interface AdvisoryType {
  id: number;
  type: string;
  backgroundColor: string;
  borderColor: string;
  textColor: string;
}

interface AdvisoryFormModalProps {
  open: boolean;
  isNewAdvisory: boolean;
  /** When true the modal is a read-only "View" — inputs disabled, Save hidden,
   *  attachments shown as downloadable links only. Used by read-only officers. */
  readOnly?: boolean;
  isSaving: boolean;
  isUploadingAttachment: boolean;
  editingAdvisory: EditingAdvisory;
  advisoryTypes: AdvisoryType[];
  recipientTypeItems: Array<{ label: string; value: string }>;
  clientItems: Array<{ label: string; value: number }>;
  clientSites: Array<{ id: number; siteName: string }>;
  isStartPickerOpen: boolean;
  isEndPickerOpen: boolean;
  theme: string;
  /** Inline field errors, keyed by field name (empty = no errors shown yet). */
  errors: AdvisoryFieldErrors;
  onSave: () => void;
  onCancel: () => void;
  onAdvisoryTypeChange: (value: string | number | null) => void;
  onRecipientTypeChange: (value: string | number | null) => void;
  onClientChange: (value: string | number | null) => void;
  onSiteIdsChange: (values: any[]) => void;
  onToggleUrgent: (value: boolean) => void;
  onToggleTemporary: (value: boolean) => void;
  onRecurringModeChange: (recurring: boolean) => void;
  onToggleRecurrenceDay: (code: string) => void;
  onRecurrenceStartTimeChange: (value: string) => void;
  onRecurrenceEndTimeChange: (value: string) => void;
  onStartDateTimeChange: (value: Date | null) => void;
  onEndDateTimeChange: (value: Date | null) => void;
  onOpenStartPicker: () => void;
  onCloseStartPicker: () => void;
  onStartPickerOpenChange: (open: boolean) => void;
  onOpenEndPicker: () => void;
  onCloseEndPicker: () => void;
  onEndPickerOpenChange: (open: boolean) => void;
  onEditingAdvisoryChange: (updater: (prev: EditingAdvisory) => EditingAdvisory) => void;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveAttachment: (attachmentId: number) => void;
  getResizedImageUrl: (attachmentId: number) => string;
}

export const AdvisoryFormModal = memo(function AdvisoryFormModal({
  open,
  isNewAdvisory,
  readOnly = false,
  isSaving,
  isUploadingAttachment,
  editingAdvisory,
  advisoryTypes,
  recipientTypeItems,
  clientItems,
  clientSites,
  isStartPickerOpen,
  isEndPickerOpen,
  theme,
  errors,
  onSave,
  onCancel,
  onAdvisoryTypeChange,
  onRecipientTypeChange,
  onClientChange,
  onSiteIdsChange,
  onToggleUrgent,
  onToggleTemporary,
  onRecurringModeChange,
  onToggleRecurrenceDay,
  onRecurrenceStartTimeChange,
  onRecurrenceEndTimeChange,
  onStartDateTimeChange,
  onEndDateTimeChange,
  onOpenStartPicker,
  onCloseStartPicker,
  onStartPickerOpenChange,
  onOpenEndPicker,
  onCloseEndPicker,
  onEndPickerOpenChange,
  onEditingAdvisoryChange,
  onFileSelect,
  onRemoveAttachment,
  getResizedImageUrl,
}: AdvisoryFormModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const officerLabel = useMemo(() => getOfficerLabel(), []);
  const officersLabel = useMemo(() => getOfficerPluralLabel(), []);

  const handleTriggerFileUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const typeItems = useMemo(
    () => advisoryTypes.map((t) => ({ label: t.type, value: t.id })),
    [advisoryTypes]
  );

  const selectedType = useMemo(
    () => advisoryTypes.find((t) => t.id === editingAdvisory.advisoryTypeId) || null,
    [advisoryTypes, editingAdvisory.advisoryTypeId]
  );

  // Plain-language confirm line restating the exact drop-off date.
  const expiryConfirmText = useMemo(() => {
    if (!editingAdvisory.endDateTime) return null;
    return moment(editingAdvisory.endDateTime).format('MMM D, YYYY [at] h:mm A');
  }, [editingAdvisory.endDateTime]);

  // End-before-start means the window ends the following day. This phrase feeds
  // both the passive note under the To field and the confirm line; null when the
  // window does not cross midnight. "Morning" when the end lands before noon.
  const overnightEndsText = useMemo(() => {
    const startMin = hhmmToMinutes(editingAdvisory.recurrenceStartTime);
    const endMin = hhmmToMinutes(editingAdvisory.recurrenceEndTime);
    if (startMin == null || endMin == null || endMin >= startMin) return null;
    return endMin < 12 * 60 ? 'the following morning' : 'the following day';
  }, [editingAdvisory.recurrenceStartTime, editingAdvisory.recurrenceEndTime]);

  // Plain-language confirm line for the recurring window. Restates the exact
  // schedule and — accurately — that the advisory STAYS on the board outside the
  // slot (marked "Outside scheduled hours"), rather than disappearing. Times are
  // in the viewer's local zone (the form always saves with the browser zone), so
  // no zone label is needed here.
  const recurringConfirm = useMemo(() => {
    const days = editingAdvisory.recurrenceDays || [];
    const start = formatHhmmForDisplay(editingAdvisory.recurrenceStartTime);
    const end = formatHhmmForDisplay(editingAdvisory.recurrenceEndTime);
    if (days.length === 0 || !start || !end) return null;
    const schedule = overnightEndsText
      ? `${formatRecurrenceDays(days, true)}, ${start} – ${end} ${overnightEndsText}`
      : `${formatRecurrenceDays(days)} ${start} – ${end}`;
    const through = editingAdvisory.endDateTime
      ? `, through ${moment(editingAdvisory.endDateTime).format('MMM D, YYYY')}`
      : '';
    return { schedule, through };
  }, [
    editingAdvisory.recurrenceDays,
    editingAdvisory.recurrenceStartTime,
    editingAdvisory.recurrenceEndTime,
    editingAdvisory.endDateTime,
    overnightEndsText,
  ]);

  return (
    <BaseFormModal
      open={open}
      title={readOnly ? `Advisory #${editingAdvisory.displayId ?? ''}` : isNewAdvisory ? 'New Advisory' : 'Edit Advisory'}
      dialogClass="kstc-dialog--form kstc-dialog--notes"
      bodyClass="kstc-body--scroll"
      saveText={
        isSaving
          ? 'Saving…'
          : isNewAdvisory
            ? 'Save Advisory'
            : 'Save Changes'
      }
      saveDisabled={isSaving || isUploadingAttachment}
      showSave={!readOnly}
      cancelText={readOnly ? 'Close' : 'Cancel'}
      busy={isSaving}
      onSave={onSave}
      onCancel={onCancel}
    >
      {/* Advisory ID (only for modify; read-only view shows it in the title) */}
      {!isNewAdvisory && !readOnly && (
        <div className="kstc-form-group">
          <label className="kstc-label">Advisory ID</label>
          <div className="kstc-display-value">
            #{editingAdvisory.displayId}
          </div>
        </div>
      )}

      {/* Type */}
      <div className="kstc-form-group">
        <label className="kstc-label kstc-label--required">Type</label>
        <div className={errors.advisoryTypeId ? 'advisory-select--error' : undefined}>
          <Dropdownlist
            value={editingAdvisory.advisoryTypeId}
            items={typeItems}
            labelKey="label"
            valueKey="value"
            placeholder="Select type…"
            searchable={false}
            disabled={readOnly}
            onChange={onAdvisoryTypeChange}
          />
        </div>
        {errors.advisoryTypeId && (
          <div className="advisory-field-error">
            <i className="fa fa-exclamation-triangle"></i> {errors.advisoryTypeId}
          </div>
        )}
        {selectedType && (
          <div style={{ marginTop: '6px' }}>
            <span
              className="advisory-type-chip"
              style={{
                display: 'inline-block',
                padding: '2px 9px',
                borderRadius: '11px',
                fontSize: '11px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                color: '#' + selectedType.textColor,
                backgroundColor: '#' + selectedType.backgroundColor,
                border: '1px solid #' + selectedType.borderColor,
              }}
            >
              {selectedType.type}
            </span>
          </div>
        )}
      </div>

      {/* Subject */}
      <div className="kstc-form-group">
        <label className="kstc-label kstc-label--required">Subject</label>
        <input
          type="text"
          className={`kstc-input${errors.subject ? ' kstc-input--error' : ''}`}
          value={editingAdvisory.subject}
          placeholder="Enter subject"
          maxLength={255}
          disabled={readOnly}
          onChange={(e) =>
            onEditingAdvisoryChange((prev) => ({
              ...prev,
              subject: e.target.value,
            }))
          }
        />
        {errors.subject && (
          <div className="advisory-field-error">
            <i className="fa fa-exclamation-triangle"></i> {errors.subject}
          </div>
        )}
      </div>

      {/* Recipients */}
      <div className="kstc-form-group">
        <label className="kstc-label kstc-label--required">Recipients</label>
        <div className={errors.recipientType ? 'advisory-select--error' : undefined}>
          <Dropdownlist
            value={editingAdvisory.recipientType}
            items={recipientTypeItems}
            labelKey="label"
            valueKey="value"
            placeholder="Select recipients…"
            searchable={false}
            disabled={readOnly}
            onChange={onRecipientTypeChange}
          />
        </div>
        {errors.recipientType && (
          <div className="advisory-field-error">
            <i className="fa fa-exclamation-triangle"></i> {errors.recipientType}
          </div>
        )}
      </div>

      {/* Client (only when recipient type is 'c') */}
      {editingAdvisory.recipientType === 'c' && (
        <div className="kstc-form-group">
          <label className="kstc-label kstc-label--required">Client</label>
          <div className={errors.npsdClient ? 'advisory-select--error' : undefined}>
            <Dropdownlist
              value={editingAdvisory.npsdClient}
              items={clientItems}
              labelKey="label"
              valueKey="value"
              placeholder="Select client…"
              searchable={clientItems.length > 5}
              disabled={readOnly}
              onChange={onClientChange}
            />
          </div>
          {errors.npsdClient && (
            <div className="advisory-field-error">
              <i className="fa fa-exclamation-triangle"></i> {errors.npsdClient}
            </div>
          )}
        </div>
      )}

      {/* Sites (only when recipient type is 'c' and client selected) */}
      {editingAdvisory.recipientType === 'c' && editingAdvisory.npsdClient && (
        <div className="kstc-form-group">
          <label className="kstc-label">Sites</label>
          <MultiSelect
            value={editingAdvisory.selectedSiteIds}
            items={clientSites}
            labelKey="siteName"
            valueKey="id"
            placeholder="Select sites…"
            disabled={readOnly}
            onChange={onSiteIdsChange}
          />
          {!readOnly && (
            <div className="kstc-help-text" style={{ marginTop: '4px' }}>
              Leave blank to post at all of this client's sites.
            </div>
          )}
        </div>
      )}

      {/* Details */}
      <div className="kstc-form-group">
        <label className="kstc-label kstc-label--required">Details</label>
        <textarea
          className={`kstc-input kstc-textarea${errors.details ? ' kstc-input--error' : ''}`}
          value={editingAdvisory.details}
          rows={5}
          placeholder="Enter details…"
          disabled={readOnly}
          onChange={(e) =>
            onEditingAdvisoryChange((prev) => ({
              ...prev,
              details: e.target.value,
            }))
          }
        />
        {errors.details && (
          <div className="advisory-field-error">
            <i className="fa fa-exclamation-triangle"></i> {errors.details}
          </div>
        )}
      </div>

      {/* Location */}
      <div className="kstc-form-group">
        <label className="kstc-label">Location</label>
        <input
          type="text"
          className="kstc-input"
          value={editingAdvisory.location}
          placeholder="e.g. North gate, Lot C, main lobby"
          maxLength={255}
          disabled={readOnly}
          onChange={(e) =>
            onEditingAdvisoryChange((prev) => ({
              ...prev,
              location: e.target.value,
            }))
          }
        />
      </div>

      {/* Urgent toggle — a single switch, not a Low/Medium/High scale. */}
      <div className="kstc-form-group">
        <label className={`advisory-urgent-toggle${editingAdvisory.urgent ? ' is-on' : ''}`}>
          <input
            type="checkbox"
            checked={editingAdvisory.urgent}
            disabled={readOnly}
            onChange={(e) => onToggleUrgent(e.target.checked)}
          />
          <span className="advisory-urgent-toggle__text">
            <span className="advisory-urgent-toggle__label">
              <i className="fa fa-exclamation-triangle"></i> Urgent — requires {officerLabel.toLowerCase()} acknowledgment
            </span>
            <span className="advisory-urgent-toggle__hint">
              Pinned first on every board; each {officerLabel.toLowerCase()} must acknowledge it individually.
            </span>
          </span>
        </label>
      </div>

      {/* Temporary (opt-in window) toggle */}
      <div className="kstc-form-group">
        <label className="kstc-checkbox-label">
          <input
            type="checkbox"
            checked={editingAdvisory.isTemporary}
            disabled={readOnly}
            onChange={(e) => onToggleTemporary(e.target.checked)}
          />
          <span style={{ marginLeft: '8px', fontWeight: 600 }}>
            Temporary Window
          </span>
        </label>
        {!readOnly && (
          <div className="kstc-help-text" style={{ marginTop: '4px' }}>
            Off = permanent. Turn on to set a one-time or recurring window; the advisory hides itself outside that window.
          </div>
        )}
      </div>

      {/* Window section — only shown when Temporary is on */}
      {editingAdvisory.isTemporary && (
        <div className="kstc-form-group advisory-window-box">
          {/* One-time vs Recurring */}
          <div className="advisory-window-modes">
            <button
              type="button"
              className={`advisory-window-mode${!editingAdvisory.recurring ? ' is-on' : ''}`}
              aria-pressed={!editingAdvisory.recurring}
              disabled={readOnly}
              onClick={() => onRecurringModeChange(false)}
            >
              <span className="advisory-window-mode__label">One-time</span>
              <span className="advisory-window-mode__hint">Start → end, then auto-hides</span>
            </button>
            <button
              type="button"
              className={`advisory-window-mode${editingAdvisory.recurring ? ' is-on' : ''}`}
              aria-pressed={editingAdvisory.recurring}
              disabled={readOnly}
              onClick={() => onRecurringModeChange(true)}
            >
              <span className="advisory-window-mode__label">Recurring</span>
              <span className="advisory-window-mode__hint">Repeats on chosen days &amp; hours</span>
            </button>
          </div>

          {/* One-time variant */}
          {!editingAdvisory.recurring && (
            <div style={{ marginTop: '12px' }}>
              <div className="kstc-form-group">
                <label className="kstc-label">Starts showing</label>
                <DateTimePicker
                  value={editingAdvisory.startDateTime}
                  type="datetime"
                  format="MM/DD/YYYY HH:mm"
                  valueType="date"
                  clearable={true}
                  appendToBody={true}
                  popupClass={theme}
                  open={isStartPickerOpen}
                  onFocus={onOpenStartPicker}
                  onClose={onCloseStartPicker}
                  onChange={onStartDateTimeChange}
                  onOpenChange={onStartPickerOpenChange}
                  disabled={readOnly}
                />
              </div>
              <div className="kstc-form-group">
                <label className="kstc-label">Auto-removes</label>
                <DateTimePicker
                  value={editingAdvisory.endDateTime}
                  type="datetime"
                  format="MM/DD/YYYY HH:mm"
                  valueType="date"
                  clearable={true}
                  appendToBody={true}
                  popupClass={theme}
                  open={isEndPickerOpen}
                  onFocus={onOpenEndPicker}
                  onClose={onCloseEndPicker}
                  onChange={onEndDateTimeChange}
                  onOpenChange={onEndPickerOpenChange}
                  disabled={readOnly}
                />
                {errors.endDateTime && (
                  <div className="advisory-field-error">
                    <i className="fa fa-exclamation-triangle"></i> {errors.endDateTime}
                  </div>
                )}
              </div>
              {!readOnly && expiryConfirmText && (
                <div className="advisory-confirm-line">
                  <i className="fa fa-info-circle"></i>
                  <span>
                    This advisory will stop showing to {officersLabel.toLowerCase()} on{' '}
                    <strong>{expiryConfirmText}</strong>. It won't be deleted, and you
                    can restore or extend it afterward.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Recurring variant — weekday chips + hours + outer date range */}
          {editingAdvisory.recurring && (
            <div style={{ marginTop: '12px' }}>
              <div className="kstc-form-group">
                <label className="kstc-label">Days of the Week</label>
                <div className="advisory-day-chips">
                  {RECURRENCE_DAY_CODES.map((code: RecurrenceDayCode) => {
                    const on = (editingAdvisory.recurrenceDays || []).includes(code);
                    return (
                      <button
                        type="button"
                        key={code}
                        className={`advisory-day-chip${on ? ' is-on' : ''}`}
                        disabled={readOnly}
                        aria-pressed={on}
                        onClick={() => onToggleRecurrenceDay(code)}
                      >
                        {RECURRENCE_DAY_LABELS[code]}
                      </button>
                    );
                  })}
                </div>
                {errors.recurrenceDays && (
                  <div className="advisory-field-error">
                    <i className="fa fa-exclamation-triangle"></i> {errors.recurrenceDays}
                  </div>
                )}
              </div>

              <div className="advisory-window-row">
                <div className="kstc-form-group">
                  <label className="kstc-label">From</label>
                  <div className={errors.recurrenceStartTime ? 'advisory-timepicker--error' : undefined}>
                    <TimePicker
                      value={editingAdvisory.recurrenceStartTime}
                      placeholder="Time"
                      disabled={readOnly}
                      onChange={onRecurrenceStartTimeChange}
                    />
                  </div>
                  {errors.recurrenceStartTime && (
                    <div className="advisory-field-error">
                      <i className="fa fa-exclamation-triangle"></i> {errors.recurrenceStartTime}
                    </div>
                  )}
                </div>
                <div className="kstc-form-group">
                  <label className="kstc-label">To</label>
                  <div className={errors.recurrenceEndTime ? 'advisory-timepicker--error' : undefined}>
                    <TimePicker
                      value={editingAdvisory.recurrenceEndTime}
                      placeholder="Time"
                      disabled={readOnly}
                      onChange={onRecurrenceEndTimeChange}
                    />
                  </div>
                  {errors.recurrenceEndTime && (
                    <div className="advisory-field-error">
                      <i className="fa fa-exclamation-triangle"></i> {errors.recurrenceEndTime}
                    </div>
                  )}
                  {!errors.recurrenceEndTime && overnightEndsText && (
                    <div className="advisory-field-note">&#x21AA; Ends {overnightEndsText}</div>
                  )}
                </div>
              </div>

              <div className="advisory-window-row">
                <div className="kstc-form-group">
                  <label className="kstc-label">Starting</label>
                  <DateTimePicker
                    value={editingAdvisory.startDateTime}
                    type="datetime"
                    format="MM/DD/YYYY HH:mm"
                    valueType="date"
                    clearable={true}
                    appendToBody={true}
                    popupClass={theme}
                    open={isStartPickerOpen}
                    onFocus={onOpenStartPicker}
                    onClose={onCloseStartPicker}
                    onChange={onStartDateTimeChange}
                    onOpenChange={onStartPickerOpenChange}
                    disabled={readOnly}
                  />
                </div>
                <div className="kstc-form-group">
                  <label className="kstc-label">Through</label>
                  <DateTimePicker
                    value={editingAdvisory.endDateTime}
                    type="datetime"
                    format="MM/DD/YYYY HH:mm"
                    valueType="date"
                    clearable={true}
                    appendToBody={true}
                    popupClass={theme}
                    open={isEndPickerOpen}
                    onFocus={onOpenEndPicker}
                    onClose={onCloseEndPicker}
                    onChange={onEndDateTimeChange}
                    onOpenChange={onEndPickerOpenChange}
                    disabled={readOnly}
                  />
                  {errors.endDateTime && (
                    <div className="advisory-field-error">
                      <i className="fa fa-exclamation-triangle"></i> {errors.endDateTime}
                    </div>
                  )}
                </div>
              </div>
              {!readOnly && (
                <div className="advisory-window-hint">
                  The schedule repeats within this date range. Leave Through empty to
                  continue until the advisory is removed.
                </div>
              )}

              {!readOnly && recurringConfirm && (
                <div className="advisory-confirm-line">
                  <i className="fa fa-info-circle"></i>
                  <span>
                    This advisory will be in effect <strong>{recurringConfirm.schedule}</strong>
                    {recurringConfirm.through}. Outside those hours it stays on the
                    board marked &ldquo;Outside scheduled hours.&rdquo; It won't be
                    deleted, and you can extend it afterward.
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Attachments */}
      <div className="kstc-form-group">
        <label className="kstc-label">Attachments</label>
        <div className="kstc-attachments">
          {!readOnly && (
            <>
              <button
                type="button"
                className="kstc-btn kstc-btn--secondary"
                onClick={handleTriggerFileUpload}
                disabled={isUploadingAttachment}
              >
                <i className="fa fa-plus"></i> Add Attachment
              </button>
              {isUploadingAttachment && (
                <span className="kstc-upload-status">Uploading…</span>
              )}
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={onFileSelect}
                accept="image/*,.pdf,.doc,.docx,.txt"
                multiple
              />
            </>
          )}

          {editingAdvisory.attachmentList &&
          editingAdvisory.attachmentList.length > 0 ? (
            <div className="kstc-attachments-list">
              {editingAdvisory.attachmentList.map(
                (attachment: any, index: number) => (
                  <div
                    className="kstc-attachment-item"
                    key={attachment.id || index}
                  >
                    {getResizedImageUrl(attachment.id) && (
                      <div className="kstc-attachment-preview">
                        <img
                          src={getResizedImageUrl(attachment.id)}
                          alt={
                            attachment.title ||
                            attachment.filename ||
                            'Attachment preview'
                          }
                        />
                      </div>
                    )}
                    <div className="kstc-attachment-info">
                      <a
                        href={attachment.signedURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="kstc-attachment-link"
                      >
                        {attachment.title ||
                          attachment.filename ||
                          'Attachment'}
                      </a>
                    </div>
                    {!readOnly && (
                      <button
                        type="button"
                        className="kstc-btn-icon kstc-btn-icon--danger"
                        onClick={() => onRemoveAttachment(attachment.id)}
                        title="Remove"
                      >
                        <i className="fa fa-trash"></i>
                      </button>
                    )}
                  </div>
                )
              )}
            </div>
          ) : (
            <div className="kstc-no-attachments">No attachments</div>
          )}
        </div>
      </div>
    </BaseFormModal>
  );
});
